import { useEffect, useRef, useState } from "react";
import { Input } from "antd5";
import classnames from "classnames";

import { Markdown } from "components/reports/builder_components/Markdown";
import { createUseDebounce } from "lib/debounce";
import { useUpdateBidReportEntryById } from "lib/hooks/api/bid/bid_report_entry/useUpdateBidReportEntryById";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventNames, useTracking } from "lib/tracking";
import { normaliseMarkdownText } from "lib/utils";
import { AnswerMarkdown } from "../../../lib/core_components/ai/AnswerMarkdown";
import { AiSource } from "../../ai_chat/types";
import toast from "../../ui/toast/Toast";
import { LoadingContent } from "../common_report_components/LoadingContent";

import css from "./EditAnswer.module.scss";

const MANUAL_PLACEHOLDER = "Click to edit";

type Props = {
  answerValue: string;
  answerId: string;
  autoQualify: boolean;
  hasEditAccess: boolean;
  isStreaming?: boolean;
  /** When provided, renders the answer with inline citation support using AnswerMarkdown */
  inlineSources?: AiSource[];
};
export function EditAnswer({
  answerValue,
  answerId,
  autoQualify,
  hasEditAccess,
  isStreaming,
  inlineSources,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState<boolean>(false);

  return (
    <div
      ref={containerRef}
      className={classnames(css.editAnswerContainer, {
        [css.editableHover]: hasEditAccess && !isEditing && !isStreaming,
      })}
      onClick={() => {
        // Only set isEditing is user has edit access
        if (hasEditAccess && !isStreaming) {
          setIsEditing(true);
        }
      }}
    >
      <EditAnswerContent
        answerValue={answerValue}
        containerRef={containerRef}
        answerId={answerId}
        autoQualify={autoQualify}
        hasEditAccess={hasEditAccess}
        isStreaming={isStreaming}
        inlineSources={inlineSources}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
      />
    </div>
  );
}

type ContentProps = {
  answerValue: string;
  containerRef: React.RefObject<HTMLDivElement>;
  answerId: string;
  autoQualify: boolean;
  hasEditAccess: boolean;
  isStreaming?: boolean;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  /** When provided, renders the answer with inline citation support using AnswerMarkdown */
  inlineSources?: AiSource[];
};
function EditAnswerContent({
  containerRef,
  answerValue,
  answerId,
  autoQualify,
  hasEditAccess,
  isStreaming,
  inlineSources,
  isEditing,
  setIsEditing,
}: ContentProps) {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();

  const [inputWidth, setInputWidth] = useState<number>();

  const { mutate: updateBidReportEntry } = useUpdateBidReportEntryById({
    onSuccess: async (data) => {
      logEvent(EventNames.qualificationCriteriaEdited, {
        "User input": data.updateBidReportEntryById?.answer?.answer ?? "",
      });

      toast.success("Bid report answer updated");
    },

    onError: () => {
      toast.error("Failed to update answer. Please try again or contact an admin.");
    },
  });

  const userDebounce500 = createUseDebounce(200);

  const debouncedUpdateAnswer = userDebounce500(updateBidReportEntry);

  // Re-sync the local display from the prop when it changes — e.g. while the
  // bid orchestrator subscription is streaming chunks into the cache, or after
  // the terminal-phase hydration replaces the streamed shape with the
  // persisted one. We skip the sync while the user is actively editing so
  // their in-progress draft doesn't get clobbered by upstream updates.
  useEffect(() => {
    if (!isEditing) {
      setAnswerDisplay(answerValue);
    }
  }, [answerValue, isEditing]);

  useEffect(() => {
    if (containerRef.current) {
      setInputWidth(containerRef.current.offsetWidth); // Set input width to match container width
    }
  }, [containerRef, isEditing]);

  const handleBlur = async () => {
    setIsEditing(false);

    if (answerDisplay === MANUAL_PLACEHOLDER || answerDisplay === answerValue || !currentUser)
      return;

    if (answerDisplay.length > 0) {
      debouncedUpdateAnswer({ input: { answer: answerDisplay, id: answerId } });
    }
  };

  const [answerDisplay, setAnswerDisplay] = useState<string>(answerValue);

  /**
   * We don't allow empty answer values in this component, otherwise the UX is
   *  a bit weird
   */
  const answerVal = () => {
    // ANSWER: Not found is a value that can sometimes be returned from the streaming subscription,
    // because it's coming from the LLm without text post-processing
    if (answerDisplay === "" || answerDisplay === "ANSWER: Not found") {
      return autoQualify ? "Not found" : MANUAL_PLACEHOLDER;
    }
    return answerDisplay;
  };

  if (isStreaming && answerValue === "") {
    return <LoadingContent />;
  }

  if (isEditing) {
    return (
      <Input.TextArea
        className={css.inputContainer}
        style={{ width: inputWidth }}
        placeholder={autoQualify ? "" : MANUAL_PLACEHOLDER}
        value={answerDisplay.replace(/\\n/g, "\n")}
        // Only update answer when focus on input container is lost
        onBlur={handleBlur}
        onChange={(e) => {
          setAnswerDisplay(e.target.value);
        }}
        autoSize
        autoFocus
      />
    );
  }

  if (inlineSources) {
    return (
      <AnswerMarkdown
        answer={normaliseMarkdownText(answerVal())}
        sources={inlineSources}
        customComponents={{
          // Custom heading renderer for h1, h2, h3
          h1: ({ children }) => <h1 className={css.heading}>{children}</h1>,
          h2: ({ children }) => <h2 className={css.heading}>{children}</h2>,
          h3: ({ children }) => <h3 className={css.heading}>{children}</h3>,
        }}
      />
    );
  }

  return (
    <Markdown
      text={normaliseMarkdownText(answerVal())}
      className={hasEditAccess ? css.editableMarkdown : css.markdown}
      targetType="new-tab"
      customComponents={{
        // Custom heading renderer for h1, h2, h3
        h1: ({ children }) => <h1 className={css.heading}>{children}</h1>,
        h2: ({ children }) => <h2 className={css.heading}>{children}</h2>,
        h3: ({ children }) => <h3 className={css.heading}>{children}</h3>,
      }}
    />
  );
}
